import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { auth, db } from '../lib/firebase';
import { doc, getDoc, setDoc, updateDoc, onSnapshot } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';

// Safari detection utility
const isSafari = () => /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mockupCount, setMockupCount] = useState(0);
  const [isNewUser, setIsNewUser] = useState(false);
  const mockupCountRef = useRef(0);

  // Initialize user data when user signs in
  const initializeUserData = async (user) => {
    if (!user || !db) return;

    try {
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (!userSnap.exists()) {
        // Create new user document with free trial
        const initialUserData = {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          mockupsRemaining: 3, // Free trial
          totalMockupsUsed: 0,
          subscriptionStatus: 'free',
          subscriptionPlan: null,
          subscriptionEndDate: null,
          createdAt: new Date(),
          lastActiveAt: new Date()
        };

        await setDoc(userRef, initialUserData);
        setUserData(initialUserData);
        setMockupCount(3);
        mockupCountRef.current = 3;
        setIsNewUser(true); // Mark as new user
      } else {
        // Load existing user data
        const data = userSnap.data();
        setUserData(data);
        const initialCount = data.mockupsRemaining || 0;
        setMockupCount(initialCount);
        mockupCountRef.current = initialCount;
        setIsNewUser(false); // Mark as existing user
      }
    } catch (error) {
      console.error('Error initializing user data:', error);
    }
  };

  // Listen for authentication state changes
  useEffect(() => {
    console.log('🔐 UserContext: Starting auth state listener');
    
    if (!auth) {
      console.log('🔐 UserContext: No auth available, setting loading to false');
      setLoading(false);
      return;
    }

    // Add timeout to prevent infinite loading (shorter for Safari)
    const timeoutDuration = isSafari() ? 5000 : 10000; // 5s for Safari, 10s for others
    
    const loadingTimeout = setTimeout(() => {
      console.warn('Auth state change timeout - forcing loading to false');
      setLoading(false);
    }, timeoutDuration);

    
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      console.log('🔐 UserContext: Auth state changed, user:', user ? 'authenticated' : 'not authenticated');
      if (isSafari()) {
        console.log('🌐 Safari: Auth state change detected');
        if (user) {
          console.log('🌐 Safari: User authenticated after redirect');
        }
      }
      clearTimeout(loadingTimeout); // Clear timeout on successful auth state change
      
      setUser(user);
      if (user) {
        console.log('🔐 UserContext: User authenticated, initializing user data');
        await initializeUserData(user);
      } else {
        console.log('🔐 UserContext: User not authenticated, clearing data');
        setUserData(null);
        setMockupCount(0);
        mockupCountRef.current = 0;
      }
      console.log('🔐 UserContext: Setting loading to false');
      setLoading(false);
    }, (error) => {
      console.error('🔐 UserContext: Auth state change error:', error);
      clearTimeout(loadingTimeout);
      
      // Safari-specific error handling
      if (isSafari() && error.message && error.message.includes('Pending promise')) {
        console.warn('Safari: Caught pending promise error in auth state change');
        // Don't set loading to false immediately for Safari
        setTimeout(() => setLoading(false), 1000);
      } else {
        setLoading(false);
      }
    });


    return () => {
      clearTimeout(loadingTimeout);
      unsubscribe();
    };
  }, []);

  // Listen for user data changes
  useEffect(() => {
    if (!user || !db) return;

    const userRef = doc(db, 'users', user.uid);
    const unsubscribe = onSnapshot(userRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data();
        const newMockupCount = data.mockupsRemaining || 0;
        const currentCount = mockupCountRef.current;
        console.log('📡 Firestore listener update - mockupsRemaining:', newMockupCount, 'current mockupCount:', currentCount);
        
        // Always update userData to keep it in sync
        setUserData(data);
        
        // Update mockupCount if it's different
        // Allow both increases (from webhook/purchases) and decreases (from usage)
        if (newMockupCount !== currentCount) {
          console.log('📡 Updating mockupCount from listener:', currentCount, '->', newMockupCount);
          setMockupCount(newMockupCount);
          mockupCountRef.current = newMockupCount;
        } else {
          console.log('📡 Listener value matches current, no update needed');
        }
      }
    });

    return unsubscribe;
  }, [user]);

  // Deduct mockup when generating
  const deductMockup = async (amount = 1) => {
    console.log('💳 deductMockup called with amount:', amount);
    console.log('💳 Current state - user:', !!user, 'db:', !!db, 'userData:', !!userData);
    
    if (!user || !db || !userData) {
      console.error('💳 Cannot deduct - missing user, db, or userData');
      return false;
    }

    console.log('💳 Current credits:', userData.mockupsRemaining);

    // Store current state for potential revert
    const previousMockupCount = userData.mockupsRemaining;
    const previousTotalUsed = userData.totalMockupsUsed;

    try {
      const userRef = doc(db, 'users', user.uid);
      const newMockupCount = Math.max(0, userData.mockupsRemaining - amount);
      const newTotalUsed = userData.totalMockupsUsed + amount;

      // Check if user has sufficient credits
      if (userData.mockupsRemaining < amount) {
        console.error('💳 Insufficient credits for enhancement');
        return false;
      }

      console.log('💳 Updating credits:', previousMockupCount, '->', newMockupCount);

      // Optimistically update local state immediately for better UX
      setMockupCount(newMockupCount);
      mockupCountRef.current = newMockupCount;
      setUserData({
        ...userData,
        mockupsRemaining: newMockupCount,
        totalMockupsUsed: newTotalUsed
      });

      console.log('💳 Optimistic update applied, updating Firestore...');

      await updateDoc(userRef, {
        mockupsRemaining: newMockupCount,
        totalMockupsUsed: newTotalUsed,
        lastActiveAt: new Date()
      });

      console.log('💳 Firestore update completed successfully');
      return true;
    } catch (error) {
      console.error('💳 Error deducting mockup:', error);
      // Revert optimistic update on error using captured previous values
      setMockupCount(previousMockupCount);
      mockupCountRef.current = previousMockupCount;
      setUserData({
        ...userData,
        mockupsRemaining: previousMockupCount,
        totalMockupsUsed: previousTotalUsed
      });
      return false;
    }
  };

  // Add mockups (for purchases)
  const addMockups = async (amount) => {
    if (!user || !db || !userData) return false;

    try {
      const userRef = doc(db, 'users', user.uid);
      const newMockupCount = userData.mockupsRemaining + amount;

      await updateDoc(userRef, {
        mockupsRemaining: newMockupCount,
        lastActiveAt: new Date()
      });

      return true;
    } catch (error) {
      console.error('Error adding mockups:', error);
      return false;
    }
  };

  // Update subscription
  const updateSubscription = async (subscriptionData) => {
    if (!user || !db) return false;

    try {
      const userRef = doc(db, 'users', user.uid);
      await updateDoc(userRef, {
        ...subscriptionData,
        lastActiveAt: new Date()
      });

      return true;
    } catch (error) {
      console.error('Error updating subscription:', error);
      return false;
    }
  };

  // Clear new user flag
  const clearNewUserFlag = () => {
    setIsNewUser(false);
  };

  const value = {
    user,
    userData,
    loading,
    mockupCount,
    deductMockup,
    addMockups,
    updateSubscription,
    isNewUser,
    clearNewUserFlag
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
